import React, { type ReactNode, Suspense, useMemo } from 'react';

import { graphql, useLazyLoadQuery } from 'react-relay';

import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { getTrialDaysLeft } from '@atlassian/upgrades-and-purchases/get-trial-days-left';

import { bytesToGigabytes } from '../services/bytes-to-gigabytes';
import {
	EditionAwarenessContext,
	type EditionAwarenessContextType,
} from '../services/edition-awareness-context';
import { getQueryConfig } from '../services/get-query-config';
import { getSiteTraitValues } from '../services/get-trait-values';
import { useSpotlight } from '../services/use-spotlight';
import type {
	Edition,
	Environment,
	PremiumWrapperProps,
	Product,
	Trait,
	TraitsForContext,
	UserRole,
} from '../types';

import type { editionAwarenessQuery } from './__generated__/editionAwarenessQuery.graphql';
import { AudienceAndTiming } from './audience-and-timing';
import { ErrorBoundary } from './error-boundary';
import { Spotlight } from './spotlight';

function getBillingSourceSystem(typename?: string | null) {
	switch (typename) {
		case 'CcpEntitlement':
			return 'CCP';
		case 'HamsEntitlement':
			return 'HAMS';
	}
}

export type EditionAwarenessProps = {
	cloudId: string;
	product: Product;
	edition: Edition;
	accountId?: string | null;
	userRoles?: UserRole[];
	navVersion?: string;
	environment?: Environment;
	siteTraits?: Trait[];

	/**
	 * This is used to inform the component that it can render, dependencies that need to be fetched
	 * outside of product should use this for the loading state.
	 *
	 * The reason for passing through `canRender` and not just returning `null` when `canRender` is false
	 * is to ensure that the edition awareness query can run as early as possible. This means that if the parameters being
	 * passed through are used in the query we can not use `canRender` for those as the query will run before we have
	 * the values.
	 */
	canRender: boolean;

	/** @deprecated dismissing the button should be handled from within the package */
	hasDismissedButton?: boolean;
	/** @deprecated dismissing the button should be handled from within the package */
	onDismissButton?: () => void;
	/** @deprecated fetching reverse trial state should be fetched within the edition awareness component */
	isInReverseTrial?: boolean;
	/** @deprecated actions from within product will move to a more generic approach */
	onOpenOnboardingMenu?: () => void;
	/** @deprecated likely to be removed in the future */
	premiumFeatureHighlightToggle?: ReactNode;
	/** @deprecated avoid auto opening the dialogs */
	shouldAutoOpen?: boolean;
	/** @deprecated avoid auto opening the dialogs */
	onAutoOpen?: () => void;

	/**
	 * Confluence in premium has a lot of specific packages that are wired up with the button,
	 * we're therefore passing it through to edition awareness to prevent fragmenting the implementation
	 * of those features (e.g. needing to replicate/shift the logic for button border nudges into platform)
	 *
	 * @deprecated this will be removed, anything new should be added directly inside of the package and not in the product
	 */
	PremiumWrapper?: (props: PremiumWrapperProps) => ReactNode;
};

/**
 * Edition awareness is used to communicate to customers about the current edition they have and if they should
 * - Add payment details
 * - Upgrade their edition
 *
 * This component deals with creating the context which is then used throughout the package to:
 * - Track analytics
 * - Know which product/edition/cloud id is being used
 * - Know when to show/hide the button
 */
function EditionAwarenessImpl(props: EditionAwarenessProps) {
	const {
		cloudId,
		product,
		edition,
		accountId,
		userRoles: userRolesFromProduct,
		navVersion,
		environment,
		siteTraits = [],
		canRender,
		hasDismissedButton,
		onDismissButton,
		isInReverseTrial: isInReverseTrialFromProduct,
		onOpenOnboardingMenu,
		premiumFeatureHighlightToggle,
		shouldAutoOpen,
		onAutoOpen,
		PremiumWrapper,
	} = props;

	const { isSpotlightActive, handleSetSpotlight } = useSpotlight();

	const queryConfig = getQueryConfig(product, edition, cloudId, accountId);

	const data = useLazyLoadQuery<editionAwarenessQuery>(
		graphql`
			query editionAwarenessQuery(
				$cloudId: ID!
				$productKey: String!
				$shouldCheckForChangeOffering: Boolean!
				$shouldCheckForTrial: Boolean!
				$shouldSkip: Boolean!
				$shouldCheckForChargeQuantities: Boolean!
				$entitlementFilter: CommerceEntitlementFilter
				$chargeElement: String!
				$shouldCheckBillingAdmin: Boolean!
				$shouldCheckForTwc: Boolean!
				$shouldCheckForStorageUsageJira: Boolean!
				$applicationKey: JiraApplicationKey!
				$shouldCheckForStorageUsageConfluence: Boolean!
				$accountId: ID!
				$shouldCheckForUserEmail: Boolean!
			) {
				jira @include(if: $shouldCheckForStorageUsageJira) {
					attachmentStorageUsed(cloudId: $cloudId, applicationKey: $applicationKey)
				}

				confluence_storage(cloudId: $cloudId) @include(if: $shouldCheckForStorageUsageConfluence) {
					bytesUsed
				}

				tenantContexts(cloudIds: [$cloudId]) @skip(if: $shouldSkip) {
					entitlementInfo(hamsProductKey: $productKey) {
						entitlement(where: $entitlementFilter) {
							__typename
							experienceCapabilities @include(if: $shouldCheckForChangeOffering) {
								changeOfferingV2(offeringName: "Standard") {
									isAvailableToUser
									experienceUrl
								}
							}

							latestUsageForChargeElement(chargeElement: $chargeElement)
								@include(if: $shouldCheckForChargeQuantities)

							preDunning @include(if: $shouldCheckForTrial) {
								status
							}

							subscription @include(if: $shouldCheckForTrial) {
								trial {
									timeLeft
									startTimestamp
									endTimestamp
								}
								accountDetails {
									invoiceGroup {
										invoiceable
										experienceCapabilities {
											configurePaymentV2 {
												experienceUrl
												isAvailableToUser
											}
										}
									}
								}
							}

							offering @include(if: $shouldCheckForTrial) {
								trial {
									lengthDays
								}
							}

							transactionAccount @include(if: $shouldCheckBillingAdmin) {
								isCurrentUserBillingAdmin
							}

							relatesFromEntitlements @include(if: $shouldCheckForTwc) {
								relationshipType
								entitlementId
							}
						}
					}
				}

				user(accountId: $accountId) @include(if: $shouldCheckForUserEmail) {
					... on AtlassianAccountUser {
						email
					}
				}
			}
		`,
		queryConfig,
	);

	const entitlement = data?.tenantContexts?.[0]?.entitlementInfo?.entitlement;

	const isInvoiceable = entitlement?.subscription?.accountDetails?.invoiceGroup?.invoiceable;

	const isConfigurePaymentAvailable =
		entitlement?.subscription?.accountDetails?.invoiceGroup?.experienceCapabilities
			?.configurePaymentV2?.isAvailableToUser === true;
	const addPaymentDetailsExperienceUrl =
		entitlement?.subscription?.accountDetails?.invoiceGroup?.experienceCapabilities
			?.configurePaymentV2?.experienceUrl;

	const canAddPaymentDetails = Boolean(
		isConfigurePaymentAvailable && addPaymentDetailsExperienceUrl,
	);

	const trialTimeLeft = entitlement?.subscription?.trial?.timeLeft ?? undefined;
	const trialDaysLeft = getTrialDaysLeft(trialTimeLeft);

	const trialStartTimestamp = entitlement?.subscription?.trial?.startTimestamp;
	const trialStartDate = useMemo(
		() => (trialStartTimestamp ? new Date(trialStartTimestamp) : undefined),
		[trialStartTimestamp],
	);

	const trialEndTimestamp = entitlement?.subscription?.trial?.endTimestamp;
	const trialEndDate = useMemo(
		() => (trialEndTimestamp ? new Date(trialEndTimestamp) : undefined),
		[trialEndTimestamp],
	);

	const trialLengthDays = entitlement?.offering?.trial?.lengthDays ?? undefined;

	const isInTrial = Boolean(trialDaysLeft && trialDaysLeft > 0);

	const isInPredunning = entitlement?.preDunning?.status === 'IN_PRE_DUNNING' && !isInvoiceable;

	const isInReverseTrial =
		(isInTrial || isInPredunning) &&
		(isInReverseTrialFromProduct !== undefined ? isInReverseTrialFromProduct : false);

	const isChangeOfferingAvailable =
		entitlement?.experienceCapabilities?.changeOfferingV2?.isAvailableToUser;
	const changeOfferingToStandardExperienceUrl =
		entitlement?.experienceCapabilities?.changeOfferingV2?.experienceUrl;

	const canUpgrade = Boolean(isChangeOfferingAvailable && changeOfferingToStandardExperienceUrl);

	const billingSourceSystem = getBillingSourceSystem(entitlement?.__typename);

	const isInTeamWorkCollection = useMemo(() => {
		const collectionEntitlement = entitlement?.relatesFromEntitlements?.find(
			(item) => item?.relationshipType === 'COLLECTION',
		);

		return !!collectionEntitlement?.entitlementId;
	}, [entitlement?.relatesFromEntitlements]);

	const userRoles: UserRole[] = useMemo(() => {
		const roles: UserRole[] = [];
		if (userRolesFromProduct?.includes('SITE_ADMIN')) {
			roles.push('SITE_ADMIN');
		}
		if (
			data?.tenantContexts?.[0]?.entitlementInfo?.entitlement?.transactionAccount
				?.isCurrentUserBillingAdmin
		) {
			roles.push('BILLING_ADMIN');
		}
		return roles;
	}, [data?.tenantContexts, userRolesFromProduct]);

	const userEmail = data?.user?.email;

	// We need to use the `latestUsageForChargeElement` to get the licensed user count for both free and trialing customers
	const licensedUsersRaw = entitlement?.latestUsageForChargeElement;
	// Assumes that there are always at least one assigned user to cover scenarios
	const licensedUsers = Math.max(licensedUsersRaw || 0, 1);

	const storageUsageInGB = useMemo(() => {
		if (
			data.jira?.attachmentStorageUsed &&
			['jira', 'jira-service-management', 'jira-product-discovery'].includes(product)
		) {
			return bytesToGigabytes(data.jira.attachmentStorageUsed);
		}

		if (data.confluence_storage?.bytesUsed && product === 'confluence') {
			return bytesToGigabytes(data.confluence_storage.bytesUsed);
		}

		return undefined;
	}, [data.jira?.attachmentStorageUsed, data.confluence_storage?.bytesUsed, product]);

	const siteTraitsForContext: TraitsForContext = useMemo(
		() => getSiteTraitValues(siteTraits),
		[siteTraits],
	);

	const context: EditionAwarenessContextType = useMemo(() => {
		return {
			cloudId,
			product,
			edition,
			isInvoiceable,
			canAddPaymentDetails,
			addPaymentDetailsExperienceUrl,
			trialDaysLeft,
			trialLengthDays,
			trialStartDate,
			trialEndDate,
			isInTrial,
			isInPredunning,
			isInReverseTrial,
			canUpgrade,
			changeOfferingToStandardExperienceUrl,
			isInTeamWorkCollection,
			userRoles,
			userEmail,
			navVersion,
			billingSourceSystem,
			environment,
			licensedUsers,
			storageUsageInGB,
			...siteTraitsForContext,
			hasDismissedButton,
			onDismissButton,
			isSpotlightActive,
			handleSetSpotlight,
			onOpenOnboardingMenu,
			premiumFeatureHighlightToggle,
			shouldAutoOpen,
			onAutoOpen,
			PremiumWrapper,
		};
	}, [
		cloudId,
		product,
		edition,
		isInvoiceable,
		canAddPaymentDetails,
		addPaymentDetailsExperienceUrl,
		trialDaysLeft,
		trialLengthDays,
		trialStartDate,
		trialEndDate,
		isInTrial,
		isInPredunning,
		isInReverseTrial,
		canUpgrade,
		changeOfferingToStandardExperienceUrl,
		isInTeamWorkCollection,
		userRoles,
		userEmail,
		navVersion,
		billingSourceSystem,
		environment,
		licensedUsers,
		storageUsageInGB,
		siteTraitsForContext,
		hasDismissedButton,
		onDismissButton,
		isSpotlightActive,
		handleSetSpotlight,
		onOpenOnboardingMenu,
		premiumFeatureHighlightToggle,
		shouldAutoOpen,
		onAutoOpen,
		PremiumWrapper,
	]);

	if (!canRender) {
		return <UFOLoadHold name="edition-awareness-waiting-on-product" />;
	}

	return (
		<EditionAwarenessContext.Provider value={context}>
			<ErrorBoundary product={product} edition={edition} id="audience-and-timing">
				<AudienceAndTiming />
				<Spotlight />
			</ErrorBoundary>
		</EditionAwarenessContext.Provider>
	);
}

export function EditionAwareness(props: EditionAwarenessProps) {
	const { product, edition } = props;

	return (
		<UFOSegment name="edition-awareness-platform">
			{/* This needs to be something that is not null, if it is null then the error will
				bubble up to the parent as `ErrorBoundary` will throw an exception if it is passed `null` */}
			<ErrorBoundary product={product} edition={edition} id="edition-awareness">
				<Suspense fallback={null}>
					<EditionAwarenessImpl {...props} />
				</Suspense>
			</ErrorBoundary>
		</UFOSegment>
	);
}
